import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useFormatMessage } from '@/translations/useFormatMessage'
import { SvgIcon } from '@mui/material'
import { Backdrop } from '@/components/common/ui/Modal/Modal.styled'
import { SignInForm } from './components/SignInForm/SignInForm'
import { VerifyOtpStep } from './components/VerifyOtpStep/VerifyOtpStep'
import {
	StyledHeaderBox,
	StyledTitle,
	StyledCloseButton,
	StyledModalCard,
} from './SignInModal.styled'
import { PATHS } from '@/router/paths'
import type { SignInModalProps, SignInModalState } from './SignInModal.types'

const TITLE_ID = 'signin-modal-title'

export function SignInModal({ isOpen, onClose, locationRef, locationId }: SignInModalProps) {
	const navigate = useNavigate()
	const { t } = useFormatMessage()
	const [state, setState] = useState<SignInModalState>({
		isVerifyStepShown: false,
		phoneOrEmailInput: '',
		otpInput: '',
		isRequestInProgress: false,
		errorMessage: null,
		correlationId: null,
		channel: null,
		identifier: null,
	})

	const handleLoginSuccess = () => {
		// Close modal immediately
		handleClose()
		// Redirect to menu (Ember-style: don't wait for customer profile)
		void navigate(PATHS.menu(locationRef))
	}

	const cardRef = useRef<HTMLDivElement>(null)
	const previousFocusRef = useRef<HTMLElement | null>(null)

	useEffect(() => {
		if (!isOpen) return

		previousFocusRef.current = document.activeElement as HTMLElement

		const firstFocusable = cardRef.current?.querySelector<HTMLElement>(
			'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
		)
		firstFocusable?.focus()

		return () => {
			previousFocusRef.current?.focus()
		}
	}, [isOpen])

	useEffect(() => {
		if (!isOpen) return
		const prev = document.body.style.overflow
		document.body.style.overflow = 'hidden'
		return () => {
			document.body.style.overflow = prev
		}
	}, [isOpen])

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === 'Escape') {
			handleClose()
			return
		}

		if (e.key !== 'Tab' || !cardRef.current) return

		const focusable = Array.from(
			cardRef.current.querySelectorAll<HTMLElement>(
				'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
			),
		)
		if (focusable.length === 0) {
			e.preventDefault()
			return
		}

		const first = focusable[0]
		const last = focusable[focusable.length - 1]

		if (e.shiftKey && document.activeElement === first) {
			e.preventDefault()
			last.focus()
		} else if (!e.shiftKey && document.activeElement === last) {
			e.preventDefault()
			first.focus()
		}
	}

	const handleStateUpdate = (updater: (prev: SignInModalState) => SignInModalState) => {
		setState(updater)
	}

	const handleClose = () => {
		setState({
			isVerifyStepShown: false,
			phoneOrEmailInput: '',
			otpInput: '',
			isRequestInProgress: false,
			errorMessage: null,
			correlationId: null,
			channel: null,
			identifier: null,
		})
		onClose()
	}

	const handleBackdropClick = () => {
		handleClose()
	}

	if (!isOpen) return null

	return (
		<Backdrop isEdit={false} role="presentation" onClick={handleBackdropClick}>
			<StyledModalCard
				ref={cardRef}
				role="dialog"
				aria-modal="true"
				aria-labelledby={TITLE_ID}
				onKeyDown={handleKeyDown}
				onClick={(e) => e.stopPropagation()}
				isEdit={false}
				width={360}
			>
				<StyledHeaderBox>
					<StyledTitle id={TITLE_ID}>
						{state.isVerifyStepShown
							? t(
									state.channel === 'EMAIL'
										? 'signin:modal:verifyEmailTitle'
										: 'signin:modal:verifyTitle',
								)
							: t('signin:modal:signInTitle')}
					</StyledTitle>
					<StyledCloseButton
						onClick={handleClose}
						aria-label={t('signin:modal:closeButton')}
						size="small"
					>
						<SvgIcon fontSize="small">
							<path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
						</SvgIcon>
					</StyledCloseButton>
				</StyledHeaderBox>

				{state.isVerifyStepShown ? (
					<VerifyOtpStep
						state={state}
						setState={handleStateUpdate}
						onClose={handleClose}
						locationRef={locationRef}
						locationId={locationId}
						onLoginSuccess={handleLoginSuccess}
					/>
				) : (
					<SignInForm
						state={state}
						setState={handleStateUpdate}
						onClose={handleClose}
						locationRef={locationRef}
						locationId={locationId}
					/>
				)}
			</StyledModalCard>
		</Backdrop>
	)
}
